import React from 'react';
import { connect } from 'react-redux';
import { checkoutSetStep, prevStep } from '../../actions/creators/checkout';
import { CHECKOUT_STEPS } from '../../reducers/checkoutData';
import { getBootstrapFilters } from '../../reducers';
import StepHeader from './StepHeader';
import Button from '../Button';
import { parseSessionIsoDateForDisplay } from '../../utils/dates';
import { orderSummaryData } from '../../selectors/orderSummaryData';
import { getMovieAttributes } from '../../utils/misc';
import styled from 'styled-components';
import { centsToCurrencyString } from '../../utils/numbers';
import { PictureWithSources } from '../common/PictureWithSources';
import { CinemaType, MovieType, TicketType, SessionType } from '../../types/entities';
import CandybarOrderSummary from './CandybarOrderSummary';
import MovieRating from '../common/MovieRating';
import NotAReceiptAlert from './common/NotAReceiptAlert';
import NotAReceiptOverlayContainer from './common/NotAReceiptOverlayContainer';

const MovieHeaderContainer = styled.div`
    align-items: flex-end;
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;

    & > picture {
        width: 40%;
        max-width: 200px;
    }

    & > div {
        display: flex;
        flex-direction: column;
        justify-content: center;

        & > *:last-child {
            margin-bottom: 0;
        }
    }

    & .title-1 {
        font-size: 2.5rem;
    }
`;

const MovieInfo = styled.div`
    display: flex;
    gap: 1.5rem;

    & > p {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        margin: 0;
    }

    & span {
        display: inline-block;
        height: 2rem;
        line-height: 2rem;
        min-width: 2rem;
        padding: 0 0.5em;
    }

    & ${MovieRating} {
        width: 2rem;
    }
`;

const MovieDuration = styled.span`
    background: #F0F0F0;
    color: #000;
    width: auto;
    white-space: nowrap;
`;

const SectionTitle = styled.div`
    align-items: center;
    border-bottom: 1px solid var(--text-primary);
    display: flex;
    justify-content: space-between;
    margin: 1.5rem 0 1rem 0;
    padding: 0.5rem 0;

    & > h3 {
        font-family: var(--ff-title);
        margin: 0;
        text-transform: uppercase;
    }
`;

const EditButton = styled.em`
    cursor: pointer;
`;

const Items = styled.div`
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr 1fr;
    margin-bottom: 1rem;
`;

const Item = styled.p`
    & > span {
        display: block;
        font-size: 0.875em;
        font-weight: 700;
        text-transform: uppercase;
    }
`;

const TicketsSummaryContainer = styled.div`
    & > div {
        display: grid;
        grid-template-columns: 3fr 1fr;
        gap: 1rem;
        margin-bottom: 1rem;
    }
`;

const Amounts = styled.div`
    border-bottom: 1px solid var(--text-primary);
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;

    & > p {
        display: grid;
        gap: 0.5rem;
        grid-template-columns: 3fr 1fr;
        margin-bottom: 0.5rem;

        & > span {
            text-transform: uppercase;
        }
    }
`;

const CouponCode = styled.p`
    align-items: center;
    display: flex;
    gap: 0.5rem;
    justify-content: space-between;

    & > span:first-child {
        color: var(--primary);
    }
`;

const Code = styled.span`
    background-color: #F0F0F0;
    font-size: 0.875em;
    padding: 0.5rem 1rem;
    text-transform: uppercase;
`;

const PaymentSummary = styled.div`
    font-size: 1.2em;
    font-weight: 700;
    margin: 2rem 0 1rem;

    & > span {
        margin-right: 0.5em;
        text-transform: uppercase;
    }
`;

interface Props {
    data: ReturnType<typeof orderSummaryData>;
    goToSeatsSelection: () => void;
    goToTicketsSelection: () => void;
    goToCandybar: () => void;
    goToPrevStep: () => void;
    sessionLanguage: string;
    sessionFormat: string;
}

const OrderReview: React.FC<Props> = (props) => {
    const { data, goToPrevStep } = props;

    return (
        <>
            <StepHeader onPrevClick={goToPrevStep} label="Resumen de compra"/>
            <NotAReceiptOverlayContainer>
                <MovieSummary {...props} />
                <CandybarSummary {...props} />
                <Payments data={data} />
            </NotAReceiptOverlayContainer>
        </>
    );
};

const MovieSummary = (props: Props) => {
    const { data, goToTicketsSelection } = props;

    if (!data.movie || !data.cinema || !data.session) {
        return null;
    }

    return (
        <>
            <MovieHeader movie={data.movie} />
            <NotAReceiptAlert altTheme={true} />
            <SessionDetails
                session={data.session}
                cinema={data.cinema}
                sessionLanguage={props.sessionLanguage}
                sessionFormat={props.sessionFormat}
            />
            <SeatsSummary
                seatsLabels={data.seatsLabels}
                goToStep={props.goToSeatsSelection}
            />
            <TicketsSummary
                order={data.ticketsOrder}
                goToStep={props.goToTicketsSelection}
            />
        </>
    )
};

const MovieHeader = ({ movie }: { movie: MovieType }) => (
    <MovieHeaderContainer>
        <PictureWithSources
            images={movie.images && movie.images.poster_medium || undefined}
        />
        <div>
            <h3 className='title-1'>{movie.name}</h3>
            {!!movie.info && (
                <MovieInfo>
                    {!!movie.info.duration && <p>Duración <MovieDuration>{movie.info.duration} min</MovieDuration></p>}
                    {!!movie.info.rating && <p>Clasificación <MovieRating>{movie.info.rating}</MovieRating></p>}
                </MovieInfo>
            )}
        </div>
    </MovieHeaderContainer>
);

const SessionDetails = ({
    session,
    cinema,
    sessionLanguage,
    sessionFormat
}: {
    session: SessionType,
    cinema: CinemaType,
    sessionLanguage: string,
    sessionFormat: string
}) => {
    const date = session ? parseSessionIsoDateForDisplay(session.datetime) : null;

    return (
        <>
            <SectionTitle>
                <h3>Detalles de la sesión</h3>
            </SectionTitle>

            <Item>
                <span>Cine</span>
                {cinema.name}
            </Item>
            <Items>
                {!!date && (
                    <>
                        <Item>
                            <span>Fecha</span>
                            {date.format('DD/MM/YYYY')}
                        </Item>
                        <Item>
                            <span>Hora</span>
                            {date.format('hh:mm A')}
                        </Item>
                    </>
                )}
                <Item>
                    <span>Idioma</span>
                    {sessionLanguage}
                </Item>
                <Item>
                    <span>Formato</span>
                    {sessionFormat}
                </Item>
            </Items>
        </>
    );
};

const SeatsSummary = ({ seatsLabels, goToStep }: { seatsLabels: Array<string>, goToStep: () => void }) => (
    <>
        <SectionTitle>
            <h3>Asientos</h3>
            <EditButton className='fal fa-pencil' onClick={goToStep}></EditButton>
        </SectionTitle>
        <Items>
            <Item>
                <span>Cantidad</span>
                {seatsLabels.length}
            </Item>
            <Item>
                <span>Asientos</span>
                {seatsLabels.join(', ')}
            </Item>
        </Items>
    </> 
);



const TicketsSummary = ({ order, goToStep }: { order: Array<TicketType & { qty: number }>, goToStep: () => void }) => (
    <>
        <SectionTitle>
            <h3>Boletos</h3>
            <EditButton className='fal fa-pencil' onClick={goToStep}></EditButton>
        </SectionTitle>
        <Item>
            <TicketsSummaryContainer>
                {order.map(ticket => <TicketRow key={ticket.id} ticket={ticket} />)}
            </TicketsSummaryContainer>
        </Item>
    </>
);

const TicketRow = ({ ticket }: { ticket: TicketType & { qty: number } }) => (
    <div>
        <div>
            {ticket.qty} x {ticket.name}
            <br/>
            {centsToCurrencyString(ticket.price)} c/u
            {ticket.description && (
                <>
                    <br/>
                    {ticket.description}
                </>
            )}
        </div>
        <div>
            {centsToCurrencyString(ticket.price * ticket.qty)}
        </div>
    </div>
);

const CandybarSummary = (props: Props) => {
    const { data } = props;

    if (!data.cinema.candybar) {
        return null;
    }
    
    const itemsIds = data.candybarOrder ? Object.keys(data.candybarOrder) : [];
    const hasItems = itemsIds.length > 0;
    
    return (
        <>
            <SectionTitle>
                <h3>Dulcería</h3>
                <EditButton className={`fal fa-${hasItems ? 'pencil' : 'plus'}`} onClick={props.goToCandybar}></EditButton>
            </SectionTitle>
            <Item>
                {/* {itemsIds.map(itemId => {
                    const item = data.candybarCatalog[itemId];
                    const qty = data.candybarOrder[itemId];
                    return (
                        <div key={itemId}>
                            {qty} x {item.name}
                        </div>
                    );
                })} */}
                <CandybarOrderSummary 
                    candybarCatalog={data.candybarCatalog}
                    candybarOrder={data.candybarOrder}
                />
            </Item>
            <Button block onClick={props.goToCandybar}>
                {hasItems ? 'Agregar más' : 'Ver dulcería'}
            </Button>
        </>
    );
};

const Payments = ({ data }) => (
    <>
    <SectionTitle>
        <h3>Resumen de compra</h3>
    </SectionTitle>

    <Amounts>
        {data.ticketsAmount > 0 && (
            <p>
                <span>Boletos</span>
                {centsToCurrencyString(data.ticketsAmount)}
            </p>
        )}
        {data.candybarAmount > 0 && (
            <p>
                <span>Dulcería</span>
                {centsToCurrencyString(data.candybarAmount)}
            </p>
        )}
        
        {data.bookingFee > 0 && (
            <p>
                <span>Costo por servicio</span>
                {centsToCurrencyString(data.bookingFee)}
            </p>
        )}
        <p>
            <span><strong>Subtotal</strong></span>
            <strong>{centsToCurrencyString(data.grandTotal)}</strong>
        </p>
    </Amounts>

    {!!data.couponCode && isCouponCodeInUse(data.ticketsOrder) && (
        <CouponCode>
            <span>Código promocional</span>
            <Code>{data.couponCode}</Code>
        </CouponCode>
    )}

    <PaymentSummary>
        <span>Total a pagar</span>
        {data.grandTotalPaymentString}
    </PaymentSummary>
    </>
);

const isCouponCodeInUse = (ticketsOrder) => {
    return ticketsOrder.some(ticket => ticket.extra.coupon || ticket.extra.recognition);
};

const mapStateToProps = (state: any) => {
    const data = orderSummaryData(state);
    const attributes = getBootstrapFilters(state);
    let sessionLanguage, sessionFormat;

    if (data.session && data.movie) {
        const movieAttributes = getMovieAttributes(data.movie);
        const languages = movieAttributes.filter(attr => attr.indexOf('lang_') === 0);
        const formats = movieAttributes.filter(attr => attr.indexOf('lang_') !== 0);
        sessionLanguage = languages.map(attr => attributes[attr].display_name).join(', ');
        sessionFormat = formats.map(attr => attributes[attr].display_name).join(', ');
    }

    return {
        data,
        sessionFormat,
        sessionLanguage,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    return {
        goToCandybar: () => dispatch(checkoutSetStep(CHECKOUT_STEPS.CANDYBAR)),
        goToPrevStep: () => dispatch(prevStep()),
        goToSeatsSelection: () => dispatch(checkoutSetStep(CHECKOUT_STEPS.SEATS)),
        goToTicketsSelection: () => dispatch(checkoutSetStep(CHECKOUT_STEPS.TICKETS)),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(OrderReview);
